import { Suspense, lazy, useEffect, useRef, useState, type CSSProperties } from "react";
import { PALE_MAP_AVAILABLE } from "./pale-map-config";

const PalePinMap = lazy(() => import("./pale-pin-map"));

/** How long after the page loads before a map in view may start on its own. */
const SETTLE_MS = 3000;

/**
 * Our pale map of one place, loaded only when the guest scrolls near it.
 *
 * `loading="lazy"` alone is not enough: Safari starts a lazy iframe several
 * screens early, so the map (about 1.6 MB of Google's code) loaded during the
 * page's first seconds. On an iPhone that work shares the page's processor
 * and kept "Book now" from responding until it finished (owner, 2026-10-15).
 *
 * Watching starts on the first scroll, or a few seconds after the page has
 * loaded: while the sections above are still filling in, the map's spot sits
 * near the top for a moment and would count as "near" too soon.
 */
export function NearViewMap({
  latitude,
  longitude,
  title,
  className,
}: {
  /** Some API rows carry coordinates as decimal strings. */
  latitude: number | string;
  longitude: number | string;
  title: string;
  className: string;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const [armed, setArmed] = useState(false);
  const [near, setNear] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (armed) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = () => setArmed(true);
    const settle = () => {
      timer = setTimeout(arm, SETTLE_MS);
    };
    window.addEventListener("scroll", arm, { once: true, passive: true });
    if (document.readyState === "complete") settle();
    else window.addEventListener("load", settle, { once: true });
    return () => {
      window.removeEventListener("scroll", arm);
      window.removeEventListener("load", settle);
      if (timer) clearTimeout(timer);
    };
  }, [armed]);

  useEffect(() => {
    if (!armed || near) return;
    const el = holder.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [armed, near]);

  /*
   * Until Google's map has drawn, its spot shows a soft placeholder with a pin
   * (owner, 2026-10-28, idea 23 in the suggestions lab): on a slow hotel
   * connection a plain grey box read as broken. Same size as the map, so
   * nothing moves when it arrives.
   */
  return (
    <div ref={holder} className={`relative ${className}`}>
      {!loaded ? (
        <div
          aria-hidden
          data-testid="map-placeholder"
          className="absolute inset-0 grid animate-pulse place-items-center bg-[#E7EAF3]"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#110E39" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.45">
            <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
            <circle cx="12" cy="9.5" r="2.5" />
          </svg>
        </div>
      ) : null}
      {near ? (
        <LocationMap
          latitude={latitude}
          longitude={longitude}
          title={title}
          onReady={() => setLoaded(true)}
          className="absolute inset-0 h-full w-full border-0"
          style={{ opacity: loaded ? 1 : 0, transition: "opacity 200ms ease-out" }}
        />
      ) : null}
    </div>
  );
}

/**
 * One place on a map, in our pale brand style. Builds without a Maps key
 * (some city sites) fall back to Google's plain embed rather than no map.
 */
export function LocationMap({
  latitude,
  longitude,
  title,
  className,
  style,
  onReady,
}: {
  /** Some API rows carry coordinates as decimal strings. */
  latitude: number | string;
  longitude: number | string;
  title: string;
  className?: string;
  style?: CSSProperties;
  onReady?: () => void;
}) {
  if (!PALE_MAP_AVAILABLE) {
    return (
      <iframe
        title={title}
        src={`https://www.google.com/maps?q=${latitude},${longitude}&output=embed`}
        loading="lazy"
        onLoad={onReady}
        className={className}
        style={style}
      />
    );
  }
  return (
    <div role="region" aria-label={title} className={className} style={style}>
      <Suspense fallback={null}>
        <PalePinMap latitude={Number(latitude)} longitude={Number(longitude)} onReady={onReady} />
      </Suspense>
    </div>
  );
}
